import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDecideTargetareListaPropunere } from "@/hooks/api/mutate/useDecideTargetareListaPropunere";
import { useGetTargetareListaPropuneri } from "@/hooks/api/query/useGetTargetareListaPropuneri";
import { useToast } from "@/hooks/use-toast";
import type { TargetareListaPropunere } from "@/types/targetare";
import { PropunereCard } from "./PropunereCard";

type Props = {
  deschis: boolean;
  onClose: () => void;
};

const MAXIM_AFISATE = 100;

const etichetaScurta = (nume: string) =>
  nume
    .replace(/\.csv$/i, "")
    .replace(/^LISTA LICENTE si AUTORIZATII\s*/i, "")
    .replace(/^LISTA[- ]*/i, "")
    .slice(0, 40);

export function PropuneriPotrivireDialog({ deschis, onClose }: Props) {
  const { toast } = useToast();
  const propuneri = useGetTargetareListaPropuneri(deschis);
  const decide = useDecideTargetareListaPropunere();
  const [filtru, setFiltru] = useState<string | null>(null);

  const fisiere = useMemo(
    () =>
      Object.entries(propuneri.data?.pe_lista ?? {}).sort(
        (a, b) => b[1] - a[1]
      ),
    [propuneri.data]
  );
  const lista = useMemo(
    () =>
      (propuneri.data?.propuneri ?? []).filter(
        (p) => filtru === null || p.nume_fisier === filtru
      ),
    [propuneri.data, filtru]
  );

  const decideCu = (propunere: TargetareListaPropunere, cui: string | null) =>
    decide.mutate(
      { id: propunere.id, cui },
      {
        onSuccess: (r) =>
          toast({
            title: cui
              ? `Confirmat: ${r.data.denumire ?? cui}`
              : `Respins: ${propunere.denumire}`,
            description: cui
              ? "Firma a fost adăugată pe listă; denumirea se va potrivi automat la importurile următoare."
              : "Denumirea nu va mai fi propusă.",
          }),
        onError: (e) =>
          toast({
            title: "Decizia nu a fost salvată",
            description: e.message,
            variant: "destructive",
          }),
      }
    );

  return (
    <Dialog open={deschis} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Propuneri de potrivire ({propuneri.data?.propuneri.length ?? 0})
          </DialogTitle>
          <DialogDescription>
            Rânduri de listă fără CUI, cu denumiri prescurtate, pentru care
            registrul ONRC oferă mai mulți candidați. Alegeți firma corectă sau
            respingeți; decizia se ține minte la importurile următoare.
          </DialogDescription>
        </DialogHeader>
        {fisiere.length > 1 && (
          <div className="flex flex-wrap gap-1">
            <Button
              size="sm"
              variant={filtru === null ? "default" : "outline"}
              onClick={() => setFiltru(null)}
            >
              Toate
            </Button>
            {fisiere.map(([nume, n]) => (
              <Button
                key={nume}
                size="sm"
                variant={filtru === nume ? "default" : "outline"}
                className="max-w-xs truncate"
                title={nume}
                onClick={() => setFiltru(nume)}
              >
                {etichetaScurta(nume)} ({n})
              </Button>
            ))}
          </div>
        )}
        <div className="flex-1 space-y-3 overflow-y-auto pr-1">
          {propuneri.isLoading && (
            <p className="text-sm text-muted-foreground">Se încarcă…</p>
          )}
          {propuneri.error && (
            <p className="text-sm text-destructive">
              Propunerile nu au putut fi încărcate: {propuneri.error.message}
            </p>
          )}
          {!propuneri.isLoading && !propuneri.error && lista.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nu există propuneri de confirmat.
            </p>
          )}
          {lista.slice(0, MAXIM_AFISATE).map((p) => (
            <PropunereCard
              key={p.id}
              propunere={p}
              ocupat={decide.isPending}
              onDecide={decideCu}
            />
          ))}
          {lista.length > MAXIM_AFISATE && (
            <p className="text-xs text-muted-foreground">
              Se afișează primele {MAXIM_AFISATE} din {lista.length}; restul
              apar pe măsură ce decideți.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
